import React, { PropsWithChildren, useEffect, useState } from 'react';

import { Input } from 'components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Minus, Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';

type searchType = 'WHERE' | 'MOVEIN' | 'PERIOD' | 'GUEST' | 'ROOM' | 'ROOMCOUNT' | 'BUDGET' | null;

type Props = {
  isActive: boolean;
  setActive: (value: searchType | null) => void;
  currentPeriod: number;
  onClose: (num: number) => void;
  handleChangePeriod: (value: number) => void;
};

const months = [3, 6, 12];
const MIN_MONTH = 1;
const MAX_MONTH = 24;

function ContractPeriodModal({
  isActive,
  setActive,
  currentPeriod,
  onClose,
  handleChangePeriod,
  children,
}: PropsWithChildren<Props>) {
  const { t } = useTranslation('search');

  const [inputValue, setInputValue] = useState(currentPeriod.toString());

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setActive('PERIOD');
      return;
    }

    const num = Number(inputValue);
    const isInvalid = isNaN(num) || inputValue === '';

    const finalValue = isInvalid ? currentPeriod : num;

    setInputValue(finalValue.toString());
    handleChangePeriod(finalValue);
    onClose(finalValue);
    setActive(null);
  };

  const handleIncrease = () => {
    if (Number(inputValue) < MAX_MONTH) {
      const next = Number(inputValue) + 1;
      setInputValue(next.toString());
      handleChangePeriod(next);
    }
  };

  const handleDecrease = () => {
    if (Number(inputValue) > MIN_MONTH) {
      const next = Number(inputValue) - 1;
      setInputValue(next.toString());
      handleChangePeriod(next);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      setInputValue('');
      return;
    }

    const normalized = e.target.value.replace(/^0+/, '');
    if (!/^\d+$/.test(normalized)) return;

    const num = Number(normalized);
    if (num > MAX_MONTH) {
      setInputValue(MAX_MONTH.toString());
      return;
    }

    setInputValue(normalized);
  };

  const handleMonthSelect = (month: number) => {
    setInputValue(month.toString());
    handleChangePeriod(month);
    onClose(month);
    setActive(null);
  };

  useEffect(() => {
    setInputValue(currentPeriod.toString());
  }, [currentPeriod]);

  const isInputEmpty = inputValue === '';

  return (
    <Popover open={isActive} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>

      <PopoverContent className="relative mt-[42px] w-[355px] rounded-modal border-none bg-white p-0" align="start">
        <div className="flex w-full flex-col gap-y-4 p-5">
          <div className="pb-1 text-b2">{t('contractPeriod')}</div>
          <div className="flex w-full flex-col gap-y-2.5">
            <div className="flex items-center gap-x-4">
              <button
                onClick={handleDecrease}
                disabled={isInputEmpty || Number(inputValue) <= MIN_MONTH}
                className={twJoin(
                  'flex h-6 w-6 items-center justify-center rounded-full border',
                  isInputEmpty || Number(inputValue) <= MIN_MONTH
                    ? 'border-gray-20 text-gray-20'
                    : 'border-primary text-primary',
                )}
                aria-label={t('decreasePeriod')}
              >
                <Minus width={24} height={24} />
              </button>

              <Input
                type="number"
                min={MIN_MONTH}
                max={MAX_MONTH}
                value={inputValue}
                onChange={handleInputChange}
                className="min-w-[60px] max-w-[80px] rounded border-none px-2 py-1 text-center text-b3 outline-none [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none"
                inputMode="numeric"
                pattern="[0-9]*"
              />
              <span className="text-b3">{t('months')}</span>
              <button
                onClick={handleIncrease}
                disabled={isInputEmpty || Number(inputValue) >= MAX_MONTH}
                className={twJoin(
                  'flex h-6 w-6 items-center justify-center rounded-full border',
                  isInputEmpty || Number(inputValue) >= MAX_MONTH
                    ? 'border-gray-20 text-gray-20'
                    : 'border-primary text-primary',
                )}
                aria-label={t('increasePeriod')}
              >
                <Plus />
              </button>
            </div>
          </div>

          <h5 className="flex h-close items-center text-b3">{t('mostlyUsed')}</h5>
          <div className="flex w-full items-center gap-x-1.5">
            {months.map((month, index) => (
              <button
                key={index}
                className={twJoin(
                  'rounded-full border px-2 py-1.5 text-c2',
                  Number(inputValue) === month
                    ? 'border-primary text-primary'
                    : 'border-gray-30 text-gray-30 hover:border-primary hover:text-primary',
                )}
                onClick={() => handleMonthSelect(month)}
              >
                {month} {t('months')}
              </button>
            ))}
          </div>

          <button
            onClick={() => handleOpenChange(false)}
            type="button"
            className="absolute right-5 top-5"
            aria-label={t('closeModal')}
          >
            <X />
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default ContractPeriodModal;
